import {
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    DocumentReference,
    Firestore
} from 'firebase/firestore';
import { User } from 'firebase/auth';

// Types
interface FetchInvoicesRequest {
    pageSize?: number;
    lastDocumentId?: string;
    lastInvoiceDate?: string;
}

interface Invoice {
    id: string;
    data: {
        salutation: string;
        firstName: string; 
        invoiceNumber: string;
        invoiceDate: string;
        address: string;
        email: string;
        mobileNumber: string;
        notes: string;
        table: any[]; // Replace with proper table item type
        createdAt: string;
    };
}

interface FetchInvoicesResponse {
    data: {
        success: boolean;
        message: string;
        invoices: Invoice[];
        hasMore: boolean;
        lastDocument: {
            id: string;
            invoiceDate: string;
        } | null;
    }
}

// function to fetch all the invoices from the database
export async function fetchInvoices(
    db: Firestore,
    user: User | null,
    request: FetchInvoicesRequest
): Promise<FetchInvoicesResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Authentication is required to perform this operation.');
        }

        // Fetch user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
            throw new Error('User not found in the database.');
        }

        const userData = userDoc.data();
        if (!userData.companyId) {
            throw new Error('User does not belong to a company.');
        }

        // Set default page size or use requested size
        const pageSize = request.pageSize || 10;

        // Build query
        let invoicesQuery = query(
            collection(db, 'companies', userData.companyId, 'invoices'),
            orderBy('invoiceDate', 'desc'),
            limit(pageSize)
        );

        // Apply pagination if lastDocumentId exists
        if (request.lastDocumentId && request.lastInvoiceDate) {
            try {
                const lastDocSnapshot = await getDoc(
                    doc(db, 'companies', userData.companyId, 'invoices', request.lastDocumentId)
                );

                if (lastDocSnapshot.exists()) {
                    invoicesQuery = query(
                        collection(db, 'companies', userData.companyId, 'invoices'),
                        orderBy('invoiceDate', 'asc'),
                        startAfter(lastDocSnapshot),
                        limit(pageSize)
                    );
                }
            } catch (error) {
                console.error('Error fetching last document:', error);
                // Continue without pagination if there's an error
            }
        }

        // Execute query
        const snapshot = await getDocs(invoicesQuery);

        const invoices: Invoice[] = [];
        snapshot.forEach((doc) => {
            invoices.push({
                id: doc.id,
                data: {
                    salutation: doc?.data()?.salutation || '',
                    firstName: doc?.data()?.firstName || '',
                    invoiceNumber: doc?.data()?.invoiceNumber || '',
                    invoiceDate: doc?.data()?.invoiceDate || '',
                    address: doc?.data()?.address || '',
                    email: doc?.data()?.email || '',
                    mobileNumber: doc?.data()?.mobileNumber || '',
                    notes: doc?.data()?.notes || '',
                    table: doc?.data()?.table || [],
                    createdAt: doc?.data()?.createdAt || ''
                }
            });
        });

        // Get information about whether there are more results
        const hasMore = invoices.length === pageSize;

        // Get the last document details for next page fetch
        const lastDoc = invoices[invoices.length - 1];
        const lastDocDetails = lastDoc ? {
            id: lastDoc.id,
            invoiceDate: lastDoc.data.invoiceDate
        } : null;

        return {
            data: {
                success: true,
                message: 'Invoices fetched successfully.',
                invoices,
                hasMore,
                lastDocument: lastDocDetails
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error fetching invoices:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch all the customers from the database
export async function fetchCustomers(
    db: Firestore,
    user: User | null
): Promise<any> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Authentication is required to perform this operation.');
        }

        // Fetch user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
            throw new Error('User not found in the database.');
        }

        const userData = userDoc.data();
        if (!userData.companyId) {
            throw new Error('User does not belong to a company.');
        }

        // Fetch customers
        const customersQuery = query(
            collection(db, 'companies', userData.companyId, 'accounts')
        );

        const snapshot = await getDocs(customersQuery);
        const customers: any[] = [];

        snapshot.forEach((doc) => {
            customers.push({
                id: doc.id,
                name: doc?.data()?.firstName + ' ' + doc?.data()?.lastName,
            });
        });

        return customers;

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error fetching customers:', error);
        throw new Error(errorMessage);
    }
}

// function to update the invoice in the database with status and recived amount
export async function updateInvoice(
    db: Firestore,
    user: User | null,
    invoiceId: string,
    status?: string,
    receivedAmount?: number
): Promise<any> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Authentication is required to perform this operation.');
        }

        // Fetch user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
            throw new Error('User not found in the database.');
        }

        const userData = userDoc.data();
        if (!userData.companyId) {
            throw new Error('User does not belong to a company.');
        }

        // Prepare update object
        const updateFields: Record<string, any> = {};
        if (status !== undefined) {
            updateFields.status = status;
        }
        if (receivedAmount !== undefined) {
            updateFields.receivedAmount = receivedAmount;
        }

        // Only perform update if there are fields to update
        if (Object.keys(updateFields).length === 0) {
            throw new Error('No fields provided to update.');
        }

        // Update invoice
        const invoiceRef = doc(db, 'companies', userData.companyId, 'invoices', invoiceId);
        await updateDoc(invoiceRef, updateFields);

        return {
            success: true,
            message: 'Invoice updated successfully.'
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error updating invoice:', error);
        throw new Error(errorMessage);
    }
}
